import React, { Fragment } from 'react'
import {
  DATE,
  FILE,
  NUMBER,
  OBJECT,
  QUESTION,
  SELECTION,
  STATIC,
  TEXT,
  TRUEFALSE,
} from '../../config'
import Number from './number'
import TrueFalse from './trueFalse'
import Date from './date'
import Text from './text'
import Selection from './selection'
import ChildObject from './ChildObject'
import List from './list'
import 'react-datepicker/dist/react-datepicker.css'
import Static from './static'
import Block from './block'
import { toast } from 'react-toastify'
import File from './file'

const DefaultLayout = (props) => {
  const {
    context,
    topRecordId,
    activate: activateOnParent,
    inLinkedObj,
    change,
    isInsideModal = false,
  } = props

  const InputFactory = (varDef, isFirstRendered) => {
    const {
      name,
      type: varType,
      isList,
    } = varDef
    const id = context.instanceId + '.' + name
    let itemValue = context.getValue(name)
    if (itemValue === null || itemValue === undefined) {
      itemValue = isList ? [] : ''
    }
    const label = context.getVarLabel(name)
    const helpText = context.getVarHelp(name)
    if (!isList || varType === SELECTION || varType === FILE) {
      const itemProps = {
        context,
        topRecordId,
        activate: isFirstRendered,
        ...varDef,
        id,
        label,
        helpText,
        unlabeled: context.useTableStyle || varDef.unlabeled || false,
      }
      return ScalarInputFactory(itemProps, itemValue, change)
    } else { // it's a list
      return (
        <List key={id} id={id} {...varDef} label={label} helpText={helpText}
              value={itemValue} topRecordId={topRecordId} context={context}
              factory={ScalarInputFactory} change={change}
              activate={isFirstRendered}/>
      )
    }
  }

  const ScalarInputFactory = (props, value, handleChange) => {
    const {
      type: varType,
      name,
      id,
    } = props
    switch (varType) {
      case TEXT:
        return (
          <Text key={id} {...props} value={value}
                onChange={(val, idx) => handleChange(name, val, idx)}/>
        )
      case NUMBER: {
        return (
          <Number key={id} {...props} value={value} places={props.decimalPlaces}
                  onChange={(val, idx) => handleChange(name, val, idx)}/>
        )
      }
      case TRUEFALSE:
        return (
          <TrueFalse key={id} {...props} checked={Boolean(value)}
                     onChange={(val, idx) => handleChange(name, val, idx)}/>
        )
      case DATE:
        return (
          <Date key={id} {...props} value={value}
                onChange={(val, idx) => handleChange(name, val, idx)}/>
        )
      case SELECTION:
        /* (Hopefully Temporary) Hack: under certain circumstances, treat a Selection variable as if it's an Object.
           The fact that something is a selection variable at all means, it's not supposed to be a "source of truth"
           in and of itself; rather, it's only a reference to an object defined somewhere else.  But in this situation:
             !props.options ->  the definition of this selection variable does not include an options expression,
                                so it can't be presented as a selection of options.
                                NOTE: all imported/pre-defined selection variables DO have options expressions!
                                So this should never happen unless somebody's got old definitions and has not updated.
             props.cacheOpts -> the compiler determined this selection variable's options could not be determined
                                via synchronous evaluation, so the selection variable will cache its most recent
                                answer in case the option object is needed synchronously and it's not available.
             inLinkedObj ->     means the variable is located in an object that is "linked" to some other external
                                object, i.e., it's not just a regular object the user is entering raw answers for.
           ... we instead present the selection variable as if it were an Object variable; that is, we let
           the end user see (but apparently not modify?) the last-cached value for the selection variable, as an object.

           Once it's the case that:
             * selection variables (in the models underlying queries) all have other queries specified
               as their "options" expression (SHOULD already be the case, barring old, non-refreshed models)
             * we are able to view/add/edit option objects on selection variables (#626, #625)
           then this hack should no longer be needed.
        */
        if (!props.options && props.cacheOpts && inLinkedObj) { // no options -> treat as object variable
          props = {
            ...props,
            type: 'object',
            style: 'popup',
          }
          console.error('Alert. Supposedly obsolete code is still active.')
          return (
            <ChildObject key={id} {...props} topRecordId={topRecordId}
                         value={value} isInsideModal={isInsideModal}/>
          )
        } else {
          return (
            <Selection key={id} {...props} value={value}
                       topRecordId={topRecordId}
                       onChange={(val, idx) => handleChange(name, val, idx)}
                       isInsideModal={isInsideModal}/>
          )
        }
      case OBJECT:
        return (
          <ChildObject key={id} {...props} topRecordId={topRecordId}
                       value={value}/>
        )
      case FILE:
        return <File key={id} {...props}
                     value={value}
                     onChange={(val, idx) => handleChange(name, val, idx)}
                     recordId={topRecordId}/>
      default :
        return null
    }
  }

  if (context) {
    const thisVarDef = context.getThisVarDef()
    let isFirstRendered = (thisVarDef.style !== 'inline') || activateOnParent ||
      null
    const relevantVarDefs = Array.from(context.relevantVarDefs())
    const definition = context.typeDef && context.typeDef.layouts &&
      context.typeDef.layouts[0]
    let renderedRows
    if (!definition) {
      // no definition; just render the relevant variables (app definitions prior to 0.4)
      renderedRows = relevantVarDefs.map(varDef => {
        if (varDef.style !== 'hidden') {
          const input = InputFactory(varDef, isFirstRendered)
          if (input) {
            isFirstRendered = null // first item rendered gets activated, all others do not
          }
          return input
        } // else
        return null
      })
    } else {
      // we have a default layout definition -- should be present in app definitions 0.4 or later
      const columnCount = definition.columnCount
      renderedRows = definition.rows.map(cols => {
        let colsUsed = 0
        const renderedCells = cols.map(cell => {
          const cellWidth = cell.span || (columnCount - (cell.offset || 0))
          let renderedCell
          if (cell.class === QUESTION) {
            const varDef = relevantVarDefs.find(
              def => def.name === cell.content)
            if (varDef && varDef.style !== 'hidden') { // it's relevant; render it
              const renderedInput = InputFactory(varDef, isFirstRendered)
              if (renderedInput) {
                isFirstRendered = null
                renderedCell = (
                  <Block key={cell.id} offset={cell.offset - colsUsed}
                         width={cellWidth} columnCount={columnCount}>
                    {renderedInput}
                  </Block>
                )
              }
            } else { // not relevant, don't render anything
              renderedCell = null
            }
          } else if (cell.class === STATIC) {
            // determine whether cell is visible
            let visible
            if (cell.expr) { // explicit visibility expression
              try {
                visible = context.evaluateExpression(cell.expr)
              } catch (e) {
                toast.error('Invalid visibility expression: ' + e.message)
                console.log(e)
                visible = false
              }
            } else if (cell.list && cell.list.length) { // list of variables to piggyback on relevance
              visible = false
              for (const varName of cell.list) {
                if (context.getVarRelevance(varName)) {
                  visible = true
                  break
                }
              }
            } else {
              visible = true
            }
            // if so, assemble the text and return jsx; otherwise return null
            if (visible && cell.content) {
              renderedCell = (
                <Block key={cell.id} offset={cell.offset - colsUsed}
                       width={cellWidth} columnCount={columnCount}>
                  <Static content={cell.content} scope={context.scope}/>
                </Block>
              )
            } else {
              renderedCell = null
            }
          }
          if (renderedCell) {
            colsUsed = cell.offset + cellWidth
          }
          return renderedCell
        })
        return <div className={'row'}
                    key={'r' + cols[0].id}>{renderedCells}</div>
      })
    }
    return (
      <Fragment>
        {renderedRows}
      </Fragment>
    )
  } // else no context available -- not yet initialized?
  return null
}

// function areEqual (prevProps, nextProps) {
//   /*
//   return true if passing nextProps to render would return
//   the same result as passing prevProps to render,
//   otherwise return false
//   */
//   if (!nextProps.scope) {
//     return false
//   }
//   return prevProps.scope === nextProps.scope && prevProps.scope.value === nextProps.scope.value
//   // note: this needs to be a lot more sophisticated, to detect any changes to the data that may impact the result of
//   // any assembled templates in this layout or its children!
// }

export default DefaultLayout // memo(DefaultLayout, areEqual)
